import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";

import { Alert } from "@/components/ui/Alert";
import { Skeleton } from "@/components/ui/Skeleton";
import { ApiError } from "@/lib/api";

import { track } from "../lib/analytics";
import { EquipmentCard } from "./EquipmentCard";
import { usePublicCategory } from "./api";

// FR-3 — /c/:category_slug. Hero banner with the category's hero
// image, one-paragraph description, then a grid of EquipmentCards
// ordered by `equipment_types.position`. Empty state when the category
// has no published+non-archived types.
export function CategoryLandingPage(): JSX.Element {
  const { category_slug = "" } = useParams<{ category_slug: string }>();
  const { data, isPending, isError, error } = usePublicCategory(category_slug);

  useEffect(() => {
    if (category_slug) track({ name: "category_viewed", category_slug });
  }, [category_slug]);

  if (isPending) {
    return (
      <div className="space-y-4" aria-busy="true">
        <Skeleton className="h-44 w-full" />
        <Skeleton className="h-6 w-1/3" />
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-64" />
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <Alert variant="destructive">
        We couldn&rsquo;t load this category.
        {error instanceof ApiError ? ` (HTTP ${error.status})` : null}
      </Alert>
    );
  }

  if (!data) return <CategoryNotFound />;

  return (
    <div className="space-y-6">
      <nav aria-label="Breadcrumb" className="text-sm text-zinc-600">
        <Link to="/" className="hover:underline">Home</Link>
        <span className="mx-1 text-zinc-400" aria-hidden="true">/</span>
        <span className="text-brand-black">{data.name}</span>
      </nav>

      <section
        aria-labelledby="category-heading"
        className="overflow-hidden rounded-lg border border-zinc-200 bg-brand-white"
      >
        {data.hero_image_url ? (
          <img
            src={data.hero_image_url}
            alt=""
            loading="eager"
            decoding="async"
            {...({ fetchpriority: "high" } as React.ImgHTMLAttributes<HTMLImageElement>)}
            className="h-40 w-full object-cover md:h-56"
          />
        ) : (
          <div className="flex h-40 w-full items-center justify-center bg-zinc-100 text-zinc-400 md:h-56">
            <span className="text-3xl font-bold tracking-tight">JDR</span>
          </div>
        )}
        <div className="p-4 md:p-6">
          <h1 id="category-heading" className="text-2xl font-semibold text-brand-black">
            {data.name}
          </h1>
          {data.description ? (
            <p className="mt-2 max-w-3xl text-sm text-zinc-700">{data.description}</p>
          ) : null}
        </div>
      </section>

      {data.types.length === 0 ? (
        <div className="rounded-md border border-dashed border-zinc-300 bg-brand-white p-8 text-center text-sm text-zinc-600">
          <p>No equipment is published in this category yet.</p>
          <p className="mt-2">
            <Link to="/" className="text-brand-red hover:underline">
              Browse all categories
            </Link>
          </p>
        </div>
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {data.types.map((t) => (
            <li key={t.id}>
              <EquipmentCard type={t} categorySlug={data.slug} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CategoryNotFound(): JSX.Element {
  return (
    <div className="rounded-md border border-zinc-200 bg-brand-white p-8 text-center">
      <h1 className="text-xl font-semibold text-brand-black">Category not found</h1>
      <p className="mt-2 text-sm text-zinc-600">
        The URL may be stale or the category was archived.
      </p>
      <p className="mt-4">
        <Link to="/" className="text-brand-red hover:underline">Browse all categories</Link>
      </p>
    </div>
  );
}
